/**
 * Reviews Redux State Module
 *
 * This module manages the state for the reviews application, which handles:
 * - Displaying and filtering product reviews
 * - Writing and submitting new reviews
 * - Managing review templates and forms
 *
 * State Structure:
 * - `ui`: UI state including filters, sorting, pagination, and write review forms
 * - `data`: Data state including reviews, products, facets, and review templates
 */
import { PayloadAction, combineReducers, createSlice } from "@reduxjs/toolkit";
import { Draft, castDraft } from "immer";

import {
    IFormUUID,
    IReviewsProductID,
    IReviewsProductTypeID,
    isoReviewsProductID,
    isoReviewsProductVariantID,
} from "../../models/nominals";
import { IReviewQuery, IReviewQueryFacets } from "../../models/reviews";
import { parseFacetValues, serializeFacetValues } from "../../utils/reviews";
import { difference, intersection, isEqualSet } from "../../utils/sets";
import { SubmitReviewStatus, WriteReviewFormMode } from "./constants";
import { defaultState, getDefaultWriteReviewFormState } from "./defaults";
import { IReduxState, IWriteReviewFormState } from "./models";
import {
    loadProduct,
    loadProducts,
    loadProductsByUUIDs,
    loadReviewTemplate,
    loadReviewTemplateByProductType,
    loadReviews,
    submitReview,
} from "./thunks";

// ============================================================================
// Helper Functions
// ============================================================================

const buildFacetValues = (
    facetValues: IReviewQuery,
    facetType: IReviewQueryFacets,
    optionID: string | number,
    isSelected: boolean,
    isExclusive = false,
): IReviewQuery => {
    const values = new Set(parseFacetValues(facetValues[facetType]));
    if (isExclusive) {
        values.clear();
    }
    optionID = `${optionID}`.trim();
    if (isSelected) {
        values.add(optionID);
    } else {
        values.delete(optionID);
    }
    return {
        ...facetValues,
        [facetType]: serializeFacetValues(values),
    };
};

const updateWriteReviewFormState = (
    state: Draft<IReduxState["ui"]>,
    formUUID: IFormUUID,
    updates: Partial<IWriteReviewFormState>,
): void => {
    const key = String(formUUID);
    if (!state.writeReviewForms[key]) {
        state.writeReviewForms[key] = castDraft(
            getDefaultWriteReviewFormState(),
        );
    }
    Object.assign(state.writeReviewForms[key], updates);
};

const handleReviewFormVariantChange = (
    state: Draft<IReduxState["ui"]>,
    actionPayload: {
        formUUID: IFormUUID;
        selectedVariant: string;
    },
): void => {
    updateWriteReviewFormState(state, actionPayload.formUUID, {
        selectedVariant: actionPayload.selectedVariant,
    });
};

const handleReviewFormTextChange = (
    state: Draft<IReduxState["ui"]>,
    actionPayload: {
        formUUID: IFormUUID;
        fieldName: string;
        fieldValue: string;
    },
): void => {
    const key = String(actionPayload.formUUID);
    if (!state.writeReviewForms[key]) {
        state.writeReviewForms[key] = castDraft(
            getDefaultWriteReviewFormState(),
        );
    }
    state.writeReviewForms[key].valuesText[actionPayload.fieldName] =
        actionPayload.fieldValue;
};

const updateCustomerInformationField = (
    state: Draft<IReduxState["ui"]>,
    update: { [fieldName: string]: string },
): void => {
    // Update field on customer information
    Object.assign(state.customerInformation, update);
    // Update field on any WaR forms
    for (const formState of Object.values(state.writeReviewForms)) {
        Object.assign(formState.valuesText, update);
    }
};

const handleReviewFormCheckboxChange = (
    state: Draft<IReduxState["ui"]>,
    actionPayload: {
        formUUID: IFormUUID;
        fieldName: string;
        fieldValue: string;
        isChecked: boolean;
    },
): void => {
    const key = String(actionPayload.formUUID);
    if (!state.writeReviewForms[key]) {
        state.writeReviewForms[key] = castDraft(
            getDefaultWriteReviewFormState(),
        );
    }
    const valuesMultiSelect = state.writeReviewForms[key].valuesMultiSelect;
    if (!valuesMultiSelect[actionPayload.fieldName]) {
        valuesMultiSelect[actionPayload.fieldName] = castDraft([]);
    }
    const valueSet = valuesMultiSelect[actionPayload.fieldName];
    const index = valueSet.indexOf(actionPayload.fieldValue);
    if (actionPayload.isChecked && index === -1) {
        valueSet.push(actionPayload.fieldValue);
    } else if (!actionPayload.isChecked && index !== -1) {
        valueSet.splice(index, 1);
    }
};

const handleReviewFormRadioChange = (
    state: Draft<IReduxState["ui"]>,
    actionPayload: {
        formUUID: IFormUUID;
        fieldName: string;
        fieldValue: string;
        isChecked: boolean;
    },
): void => {
    if (!actionPayload.isChecked) {
        return;
    }
    handleReviewFormTextChange(state, actionPayload);
};

// ============================================================================
// UI Slice
// ============================================================================

const uiSlice = createSlice({
    name: "reviews/ui",
    initialState: defaultState.ui,
    reducers: {
        toggleMobileSideNav: (state) => {
            state.mobileSideNavOpen = !state.mobileSideNavOpen;
        },

        updateSorting: (
            state,
            action: PayloadAction<IReduxState["ui"]["selectedSortOption"]>,
        ) => {
            state.selectedSortOption = action.payload;
        },

        setFacetValues: (state, action: PayloadAction<IReviewQuery>) => {
            state.facetValues = action.payload;
        },

        setProductTypes: (
            state,
            action: PayloadAction<readonly IReviewsProductTypeID[]>,
        ) => {
            state.productTypeIDs = castDraft(action.payload);
        },

        updateProductTypes: (
            state,
            action: PayloadAction<{
                productTypeID: IReviewsProductTypeID;
                isSelected: boolean;
            }>,
        ) => {
            const productTypeIDs = new Set(state.productTypeIDs);
            if (action.payload.isSelected) {
                productTypeIDs.add(action.payload.productTypeID);
            } else {
                productTypeIDs.delete(action.payload.productTypeID);
            }
            state.productTypeIDs = Array.from(productTypeIDs);
        },

        updateFilterOptionValue: (
            state,
            action: PayloadAction<{
                facetType: IReviewQueryFacets;
                optionID: string | number;
                isSelected: boolean;
                isExclusive?: boolean;
            }>,
        ) => {
            state.facetValues = buildFacetValues(
                state.facetValues,
                action.payload.facetType,
                action.payload.optionID,
                action.payload.isSelected,
                action.payload.isExclusive,
            );
        },

        updateFilterOptionCollectionValue: (
            state,
            action: PayloadAction<{
                facetType: IReviewQueryFacets;
                options: ReadonlyArray<{
                    readonly option_id: string | number;
                    readonly sub_option_type?: IReviewQueryFacets | null;
                    readonly sub_options?: ReadonlyArray<{
                        readonly option_id: string | number;
                    }>;
                }>;
                isSelected: boolean;
            }>,
        ) => {
            state.facetValues = action.payload.options.reduce(
                (memoA, option) => {
                    const subOptionType = option.sub_option_type;
                    if (
                        subOptionType &&
                        option.sub_options &&
                        option.sub_options.length > 0
                    ) {
                        memoA = option.sub_options.reduce(
                            (memoB, subOption) =>
                                buildFacetValues(
                                    memoB,
                                    subOptionType,
                                    subOption.option_id,
                                    action.payload.isSelected,
                                ),
                            memoA,
                        );
                    }
                    return buildFacetValues(
                        memoA,
                        action.payload.facetType,
                        option.option_id,
                        action.payload.isSelected,
                    );
                },
                state.facetValues,
            );
        },

        updateSourceFilterVisibility: (
            state,
            action: PayloadAction<{
                isOpen: boolean;
            }>,
        ) => {
            state.sourceFilterOpen = action.payload.isOpen;
            state.facetValues = {
                ...state.facetValues,
                source_id: "",
            };
        },

        clearFilters: (state) => {
            state.productTypeIDs = [];
            state.facetValues = {};
        },

        registerNewWriteReviewForm: (
            state,
            action: PayloadAction<{
                formUUID: IFormUUID;
                mode: WriteReviewFormMode;
            }>,
        ) => {
            updateWriteReviewFormState(state, action.payload.formUUID, {
                mode: action.payload.mode,
            });
        },

        setWriteReviewFormMode: (
            state,
            action: PayloadAction<{
                formUUID: IFormUUID;
                mode: WriteReviewFormMode;
            }>,
        ) => {
            updateWriteReviewFormState(state, action.payload.formUUID, {
                mode: action.payload.mode,
            });
        },

        reviewFormVariantChange: (
            state,
            action: PayloadAction<{
                formUUID: IFormUUID;
                selectedVariant: string;
            }>,
        ) => {
            handleReviewFormVariantChange(state, action.payload);
        },

        reviewFormTextChange: (
            state,
            action: PayloadAction<{
                formUUID: IFormUUID;
                fieldName: string;
                fieldValue: string;
            }>,
        ) => {
            handleReviewFormTextChange(state, action.payload);
        },

        reviewFormCheckboxChange: (
            state,
            action: PayloadAction<{
                formUUID: IFormUUID;
                fieldName: string;
                fieldValue: string;
                isChecked: boolean;
            }>,
        ) => {
            handleReviewFormCheckboxChange(state, action.payload);
        },

        reviewFormRadioChange: (
            state,
            action: PayloadAction<{
                formUUID: IFormUUID;
                fieldName: string;
                fieldValue: string;
                isChecked: boolean;
            }>,
        ) => {
            handleReviewFormRadioChange(state, action.payload);
        },

        reviewSetCustomerInformationEmail: (
            state,
            action: PayloadAction<string>,
        ) => {
            updateCustomerInformationField(state, {
                email_collection: action.payload,
            });
        },

        reviewSetCustomerInformationName: (
            state,
            action: PayloadAction<string>,
        ) => {
            updateCustomerInformationField(state, {
                name: action.payload,
            });
        },

        reviewSetCustomerInformationLocation: (
            state,
            action: PayloadAction<string>,
        ) => {
            updateCustomerInformationField(state, {
                location: action.payload,
            });
        },
    },
    extraReducers: (builder) => {
        // Handle loadReviewTemplate thunk
        builder.addCase(loadReviewTemplate.fulfilled, (state, action) => {
            if (action.payload) {
                updateWriteReviewFormState(state, action.payload.formUUID, {
                    selectedProductID: action.payload.productID,
                });
            }
        });

        // Handle loadReviewTemplateByProductType thunk
        builder.addCase(
            loadReviewTemplateByProductType.fulfilled,
            (state, action) => {
                if (action.payload) {
                    updateWriteReviewFormState(state, action.payload.formUUID, {
                        selectedProductID: action.payload.productID,
                    });
                }
            },
        );

        // Handle submitReview thunk
        builder.addCase(submitReview.fulfilled, (state, action) => {
            const {
                formUUID,
                status,
                errors,
                showErrors,
                showProductSelectError,
                showVariantSelectError,
            } = action.payload;

            // If submission succeeded, set form mode to THANK_YOU_OPEN
            if (status === SubmitReviewStatus.SUCCESS) {
                updateWriteReviewFormState(state, formUUID, {
                    mode: WriteReviewFormMode.THANK_YOU_OPEN,
                });
                return;
            }

            // If submission failed with validation errors, update the form errors
            if (errors !== undefined) {
                updateWriteReviewFormState(state, formUUID, {
                    errors: errors,
                    showErrors: showErrors || false,
                    showProductSelectError: showProductSelectError || false,
                    showVariantSelectError: showVariantSelectError || false,
                });
            }
        });

        // Handle loadReviews thunk
        builder.addCase(loadReviews.fulfilled, (state, action) => {
            state.page = action.payload.page;
            state.hasMore = action.payload.hasMore;
            state.loadedFacetValues = action.payload.loadedFacetValues;
        });
    },
});

// ============================================================================
// Data Slice
// ============================================================================

const dataSlice = createSlice({
    name: "reviews/data",
    initialState: defaultState.data,
    reducers: {},
    extraReducers: (builder) => {
        // Handle loadReviewTemplate thunk - store template in data state
        builder.addCase(loadReviewTemplate.fulfilled, (state, action) => {
            if (action.payload) {
                state.writeReviewTemplates = castDraft(
                    state.writeReviewTemplates
                        .filter(
                            ({ productID }) =>
                                productID !== action.payload.productID,
                        )
                        .concat([
                            {
                                productID: action.payload.productID,
                                template: castDraft(action.payload.template),
                            },
                        ]),
                );
            }
        });

        // Handle loadReviewTemplateByProductType thunk - store template in data state
        builder.addCase(
            loadReviewTemplateByProductType.fulfilled,
            (state, action) => {
                if (action.payload && action.payload.productID) {
                    state.writeReviewTemplates = castDraft(
                        state.writeReviewTemplates
                            .filter(
                                ({ productID }) =>
                                    productID !== action.payload?.productID,
                            )
                            .concat([
                                {
                                    productID: action.payload.productID,
                                    template: castDraft(
                                        action.payload.template,
                                    ),
                                },
                            ]),
                    );
                }
            },
        );

        // Handle loadReviews thunk - store reviews and facets
        builder.addCase(loadReviews.fulfilled, (state, action) => {
            state.loadedInitialReviews = true;
            // Determine if we should append or replace reviews based on page number
            const shouldAppend = action.payload.page > 1;
            state.reviews = castDraft(
                shouldAppend
                    ? [...state.reviews, ...action.payload.reviews]
                    : action.payload.reviews,
            );
            state.facets = castDraft(action.payload.facets);
        });

        // Handle loadProducts thunk
        builder.addCase(loadProducts.fulfilled, (state, action) => {
            state.loadedInitialProducts = true;
            state.products = castDraft(action.payload);
        });

        // Handle loadProductsByUUIDs thunk
        builder.addCase(loadProductsByUUIDs.fulfilled, (state, action) => {
            state.loadedInitialProducts = true;
            state.products = castDraft(action.payload);
        });

        // Handle loadProduct thunk
        builder.addCase(loadProduct.fulfilled, (state, action) => {
            state.loadedInitialProducts = true;
            state.products = castDraft([action.payload]);
        });
    },
});

// ============================================================================
// Combined Reducer with Post-Processing
// ============================================================================

const _innerReducers = combineReducers({
    ui: uiSlice.reducer,
    data: dataSlice.reducer,
});

/**
 * Reviews reducer with post-processing for facet value calculations.
 *
 * IMPORTANT: This reducer includes critical post-processing logic that maintains
 * the relationship between product types, products, and variants in the facet system.
 * The post-processing:
 *
 * 1. Ensures product variants are deselected when their product type is deselected
 * 2. Links product_id and product_variant_id facets together:
 *    - If all variants are selected, the main product is also selected
 *    - If only some variants are selected, the main product is deselected
 * 3. Opens the source filter automatically when a source filter is set
 *
 * This logic cannot be removed or simplified without breaking the filter UX.
 */
export const reducers: typeof _innerReducers = (oldState, action) => {
    // Run main reducers
    const newState = _innerReducers(oldState, action);

    // If none of the facet values changed, we don't need to re-do the calculations below
    const facetValuesSame =
        newState.ui.facetValues === oldState?.ui?.facetValues;
    const productTypesSame =
        newState.ui.productTypeIDs === oldState?.ui?.productTypeIDs;
    if (facetValuesSame && productTypesSame) {
        return newState;
    }

    // Build up some utility data structures to aide in tweaking the selected facet values
    const selectedProductTypeIDs = new Set<IReviewsProductTypeID>(
        newState.ui.productTypeIDs,
    );
    const variantIDMapping = new Map<
        IReviewsProductID,
        Set<ReturnType<typeof isoReviewsProductVariantID.wrap>>
    >(
        newState.data.products.map((product) => [
            product.id,
            new Set(product.variants.map((v) => v.id)),
        ]),
    );
    const facetValueProductIDs = new Set(
        parseFacetValues(newState.ui.facetValues.product_id).map((pid) =>
            isoReviewsProductID.wrap(parseInt(pid, 10)),
        ),
    );
    let facetValueVariantIDs = new Set(
        parseFacetValues(newState.ui.facetValues.product_variant_id).map(
            (vid) => isoReviewsProductVariantID.wrap(parseInt(vid, 10)),
        ),
    );

    // Apply tweaks to the facet value data
    for (const product of newState.data.products) {
        // Get all the variant IDs for this product
        const productVariantIDs = variantIDMapping.get(product.id) || new Set();

        // If this products type is not select, de-select the product and all it's variants
        const hasType = product.product_type !== null;
        const typeIsSelected =
            product.product_type &&
            selectedProductTypeIDs.has(product.product_type);
        if (hasType && !typeIsSelected) {
            facetValueProductIDs.delete(product.id);
            facetValueVariantIDs = difference(
                facetValueVariantIDs,
                productVariantIDs,
            );
        }

        // If this product has variants, link the product_id and product_variant_id facets together
        if (productVariantIDs.size > 0) {
            // Figure out which of this products variants are actually selected right now
            const selectedProductVariantIDs = intersection(
                productVariantIDs,
                facetValueVariantIDs,
            );
            if (isEqualSet(selectedProductVariantIDs, productVariantIDs)) {
                // If all of the products variants are selected, select the main product also.
                facetValueProductIDs.add(product.id);
            } else {
                if (selectedProductVariantIDs.size > 0) {
                    // Otherwise, only some of the variants are selected, so de-select the main product.
                    facetValueProductIDs.delete(product.id);
                }
            }
        }
    }

    // Return the new state
    return {
        ...newState,
        ui: {
            ...newState.ui,
            // Open the source filter if it's set to open OR if a source filter is set.
            sourceFilterOpen:
                newState.ui.sourceFilterOpen ||
                !!newState.ui.facetValues.source_id,
            // Serialize the tweaked facet values
            facetValues: {
                ...newState.ui.facetValues,
                product_id: serializeFacetValues(facetValueProductIDs),
                product_variant_id: serializeFacetValues(facetValueVariantIDs),
            },
        },
    };
};

// ============================================================================
// Export Action Creators
// ============================================================================

export const {
    toggleMobileSideNav,
    updateSorting,
    setFacetValues,
    setProductTypes,
    updateProductTypes,
    updateFilterOptionValue,
    updateFilterOptionCollectionValue,
    updateSourceFilterVisibility,
    clearFilters,
    registerNewWriteReviewForm,
    setWriteReviewFormMode,
    reviewFormVariantChange,
    reviewFormTextChange,
    reviewFormCheckboxChange,
    reviewFormRadioChange,
    reviewSetCustomerInformationEmail,
    reviewSetCustomerInformationName,
    reviewSetCustomerInformationLocation,
} = uiSlice.actions;
